import { formatDate, timeAgo } from '@bpinternal/shared'
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@bpinternal/ui-kit-next'
import { Ellipsis, EllipsisVertical, ExternalLink } from 'lucide-react'
import { PageTitleInput } from '~/components/PageTitleInput'
import { Badge, StatusLabel } from '~/elements'
import { BotCardDropdown } from '~/features/home/components'
import { useIsAuthorized, useRenameBot, useWebchatEmbedScript } from '~/hooks'
import { useQuery } from '~/services'
import { useLocalStore } from '~/stores'
import { cn } from '~/utils'
import { ConversationThumbnail } from './ConversationThumbnail'
import { EditInStudioButton } from './EditInStudioButton'
import './styles.css'

const CHANNELS = [
  'webchat',
  'chat',
  'slack',
  'teams',
  'telegram',
  'facebook',
  'messenger',
  'instagram',
  'freshchat',
  'twilio',
  'discord',
  'whatsapp',
  'line',
  'viber',
  'hitl',
]

type Props = {
  workspaceId: string
  botId: string
  userId: string
  className?: string
}

export const AgentInfoCard = ({ workspaceId, botId, userId, className }: Props) => {
  const { data: bot } = useQuery('workspaces_/$workspaceId_/bots_/$botId_', {
    workspaceId,
    botId,
  })

  const { mutate: renameBot, isPending, isSuccess } = useRenameBot()

  const authorize = useIsAuthorized({ workspaceId, userId })
  const isAuthorizedToUpdateBot = authorize('bots.update')
  const { shareableLink } = useWebchatEmbedScript({ botId, workspaceId })
  const theme = useLocalStore((s) => s.getPreferredTheme())

  return (
    <div className={cn('flex w-full flex-col rounded-xl bg-neutral-50 dark:bg-neutral-100 sm:flex-row', className)}>
      <div className="bg-grid group relative h-60 w-full overflow-hidden rounded-l-xl sm:h-auto sm:w-1/3 sm:max-w-96">
        <ConversationThumbnail
          workspaceId={workspaceId}
          botId={botId}
          themeOverride={theme}
          className="absolute bottom-0 left-1/2 -translate-x-1/2"
        />
        <a
          className="absolute right-8 top-8 hidden group-hover:flex max-md:flex"
          href={shareableLink ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button variant="secondary">
            <ExternalLink />
            Preview
          </Button>
        </a>
      </div>
      <div className="flex w-full min-w-0 max-w-full flex-1 flex-col gap-6 p-6">
        <div className="flex w-full items-center justify-between gap-4">
          <PageTitleInput
            defaultName={bot?.name || 'Bot'}
            onRename={(newName: string) => {
              renameBot({
                workspaceId,
                botId,
                name: newName,
              })
            }}
            isPending={isPending}
            isSuccess={isSuccess}
            maxLength={64}
            showError
            className="flex-1 font-bold"
          />
          <div className="flex-0 flex min-w-0 max-w-full items-center gap-2">
            <EditInStudioButton className="hidden sm:flex" workspaceId={workspaceId} botId={botId} userId={userId} />
            {isAuthorizedToUpdateBot && (
              <BotCardDropdown userId={userId} botId={botId} botName={bot?.name || 'Bot'} workspaceId={workspaceId}>
                <Button variant="ghost" className="flex w-5 flex-none" onClick={(e) => e.stopPropagation()}>
                  <EllipsisVertical className="hidden !h-5 !w-5 sm:block" />
                  <Ellipsis className="!h-5 !w-5 sm:hidden" />
                </Button>
              </BotCardDropdown>
            )}
          </div>
        </div>
        <div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-muted-foreground">Last Deployed</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-fit">{bot?.deployedAt && timeAgo(bot.deployedAt)}</span>
              </TooltipTrigger>
              <TooltipContent>
                <span>{bot?.deployedAt && formatDate(bot.deployedAt, { format: 'medium', includeTime: true })}</span>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
        <div className="flex gap-12">
          <div className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-muted-foreground">Status</span>
            <StatusLabel isEnabled={bot?.status === 'active'} enabledLabel="Online" disabledLabel="Offline" />
          </div>
          <div className="flex flex-col gap-2 text-sm">
            <span className="font-medium text-muted-foreground">Always Alive</span>
            <StatusLabel isEnabled={bot?.alwaysAlive ?? false} enabledLabel="Enabled" disabledLabel="Disabled" />
          </div>
        </div>
        <div className="flex flex-col gap-2 text-sm">
          <span className="font-medium text-muted-foreground">Deployed Channels</span>
          <div className="flex flex-wrap gap-2">
            {bot?.integrations && Object.values(bot.integrations).length > 0 ? (
              Object.values(bot.integrations)
                .filter((integration) => CHANNELS.includes(integration.name))
                .map((integration) => (
                  <Badge
                    key={integration.id}
                    variant="outline"
                    className="flex items-center gap-1 rounded-lg px-2 py-1"
                  >
                    <img src={integration.iconUrl} alt={integration.title} className="h-4 w-4" />
                    {integration.title}
                  </Badge>
                ))
            ) : (
              <span>None</span>
            )}
          </div>
        </div>
        <EditInStudioButton className="flex sm:hidden" workspaceId={workspaceId} botId={botId} userId={userId} />
      </div>
    </div>
  )
}
